import React from 'react';
import { getEmployeeTypeDisplayName } from '../services/api.ts';

// The fields of one provider record (create or edit). Purely presentational: the parent owns the value,
// saving and errors — see ProviderFormDialog.

export type ProviderType = 'nurse' | 'doctor' | 'nurse-student-junior' | 'nurse-student-senior';

export interface ProviderFormValues {
  employeeType: ProviderType;
  firstName: string;
  lastName: string;
  homeHospitalId: string;
  credentials: string[];
  isFloatable: boolean;
  maxConcurrentNurseCoverageRatio: number;
  maxConcurrentStudentCoverageRatio: number;
  isCardiacAnesthesiologist: boolean;
  isPediatricAnesthesiologist: boolean;
  studentCompetency: '' | 'new' | 'intermediate' | 'green';
  isPregnant: boolean;
}

/** A blank form — the one definition of a new provider's defaults. */
export const EMPTY_PROVIDER_FORM: ProviderFormValues = Object.freeze({
  employeeType: 'nurse',
  firstName: '',
  lastName: '',
  homeHospitalId: '',
  credentials: [],
  isFloatable: true,
  maxConcurrentNurseCoverageRatio: 4,
  maxConcurrentStudentCoverageRatio: 2,
  isCardiacAnesthesiologist: false,
  isPediatricAnesthesiologist: false,
  studentCompetency: '',
  isPregnant: false,
}) as ProviderFormValues;

/** The form pre-filled from an existing provider record (absent fields fall back to the defaults). */
export const providerFormFromEmployee = (employee: any): ProviderFormValues => ({
  employeeType: employee.employeeType,
  firstName: employee.firstName,
  lastName: employee.lastName,
  homeHospitalId: employee.homeHospitalId || '',
  credentials: employee.credentials || [],
  isFloatable: employee.isFloatable !== false,
  maxConcurrentNurseCoverageRatio: employee.maxConcurrentNurseCoverageRatio || 4,
  maxConcurrentStudentCoverageRatio: employee.maxConcurrentStudentCoverageRatio || 2,
  isCardiacAnesthesiologist: employee.isCardiacAnesthesiologist || false,
  isPediatricAnesthesiologist: employee.isPediatricAnesthesiologist || false,
  studentCompetency: employee.studentCompetency || '',
  isPregnant: employee.isPregnant || false,
});

interface ProviderFormProps {
  value: ProviderFormValues;
  onChange: (next: ProviderFormValues) => void;
  hospitals: Array<{ hospitalId: string; name: string }>;
  credentialOptions: string[];
  credentialsError?: string | null;
}

const ProviderForm: React.FC<ProviderFormProps> = ({ value, onChange, hospitals, credentialOptions, credentialsError }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    onChange({ ...value, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, [e.target.name]: e.target.checked });
  };

  const handleCredentialsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const credential = e.target.value;
    onChange({
      ...value,
      credentials: e.target.checked
        ? [...value.credentials, credential]
        : value.credentials.filter(c => c !== credential),
    });
  };

  // Credential options = the credential catalog PLUS every hospital (so all facilities are selectable),
  // plus anything already on this provider (so existing picks always show). Matches the Credentials table.
  const credentialChoices = (() => {
    const s = new Set<string>();
    for (const c of credentialOptions) if (c && c.trim()) s.add(c);
    for (const h of hospitals) if (h && h.name && h.name.trim()) s.add(h.name);
    for (const c of value.credentials) if (c) s.add(c);
    return [...s].sort((a, b) => a.localeCompare(b));
  })();

  return (
    <>
      {credentialsError && (
        <div className="alert alert-error">{credentialsError}</div>
      )}

      <div className="form-group">
        <label htmlFor="employeeType">Provider Type</label>
        <select
          id="employeeType"
          name="employeeType"
          value={value.employeeType}
          onChange={handleChange}
          required
        >
          <option value="nurse">{getEmployeeTypeDisplayName('nurse')}</option>
          <option value="doctor">{getEmployeeTypeDisplayName('doctor')}</option>
          <option value="nurse-student-junior">{getEmployeeTypeDisplayName('nurse-student-junior')}</option>
          <option value="nurse-student-senior">{getEmployeeTypeDisplayName('nurse-student-senior')}</option>
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="firstName">First Name</label>
        <input
          type="text"
          id="firstName"
          name="firstName"
          value={value.firstName}
          onChange={handleChange}
          required
          placeholder="Enter first name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="lastName">Last Name</label>
        <input
          type="text"
          id="lastName"
          name="lastName"
          value={value.lastName}
          onChange={handleChange}
          required
          placeholder="Enter last name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="homeHospitalId">Home Hospital (Optional)</label>
        <select
          id="homeHospitalId"
          name="homeHospitalId"
          value={value.homeHospitalId}
          onChange={handleChange}
        >
          <option value="">-- Select Hospital --</option>
          {hospitals.map(hospital => (
            <option key={hospital.hospitalId} value={hospital.hospitalId}>
              {hospital.name}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="form-group" style={{ border: 'none', padding: 0, margin: '0 0 20px' }}>
        <legend style={{ fontWeight: 500, marginBottom: '8px' }}>Credentials (Optional)</legend>
        <div className="credentials-checkboxes">
          {credentialChoices.map(credential => (
            <label key={credential} className="checkbox-label">
              <input
                type="checkbox"
                value={credential}
                checked={value.credentials.includes(credential)}
                onChange={handleCredentialsChange}
              />
              <span className="checkbox-text">{credential}</span>
            </label>
          ))}
          {credentialChoices.length === 0 && !credentialsError && (
            <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No credentials available</div>
          )}
        </div>
      </fieldset>

      <div className="form-group" style={{ borderTop: '1px solid var(--border, #e2e8f0)', paddingTop: '16px', marginTop: '4px' }}>
        <label style={{ fontWeight: 600 }}>Radiation Safety</label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isPregnant"
            checked={value.isPregnant}
            onChange={handleCheckboxChange}
          />
          <span className="checkbox-text">
            {value.employeeType === 'doctor'
              ? 'Pregnant — avoid solo (doctor-only) ionizing-radiation rooms'
              : 'Pregnant — never place in an ionizing-radiation room'}
          </span>
        </label>
        <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
          {value.employeeType === 'doctor' ? (
            <>
              When checked, this anesthesiologist is not assigned <strong>solo</strong> (doctor-only)
              to a room with ionizing radiation (interventional radiology, EP lab, cath lab, vascular,
              orthopedic C-arm / bone cement, urology fluoroscopy, ERCP, angio/embolization).
              Supervising CRNAs in such a room is fine — a floating supervisor isn&apos;t continuously exposed.
            </>
          ) : (
            <>
              When checked, this person (CRNA or student nurse) is never allocated to a room with
              ionizing radiation (interventional radiology, EP lab, cath lab, vascular, orthopedic
              C-arm / bone cement, urology fluoroscopy, ERCP, angio/embolization). Those rooms are
              staffed by other (non-pregnant) staff instead; they stay available for all other cases
              (robotic, laparoscopic, EGD/colonoscopy, CABG, ENT, etc.).
            </>
          )}
        </small>
      </div>

      {value.employeeType === 'doctor' && (
        <>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isFloatable"
                checked={value.isFloatable}
                onChange={handleCheckboxChange}
              />
              <span className="checkbox-text">Can Float (allocate to multiple ORs concurrently)</span>
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
              If unchecked, doctor can only be allocated to one OR at a time
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isCardiacAnesthesiologist"
                checked={value.isCardiacAnesthesiologist}
                onChange={handleCheckboxChange}
              />
              <span className="checkbox-text">Cardiac Anesthesiologist</span>
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
              Symphony staffs cardiac rooms (CABG, TAVR, aortic) only with cardiac anesthesiologists
            </small>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isPediatricAnesthesiologist"
                checked={value.isPediatricAnesthesiologist}
                onChange={handleCheckboxChange}
              />
              <span className="checkbox-text">Pediatric Anesthesiologist</span>
            </label>
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
              Only pediatric anesthesiologists may supervise at a children's hospital
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="maxConcurrentNurseCoverageRatio">Max Concurrent CRNA Coverage</label>
            <input
              type="number"
              id="maxConcurrentNurseCoverageRatio"
              name="maxConcurrentNurseCoverageRatio"
              value={value.maxConcurrentNurseCoverageRatio}
              onChange={handleChange}
              min="1"
              max="10"
              placeholder="Default: 4"
            />
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
              Max number of ORs with CRNAs this doctor can cover concurrently (default: 4)
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="maxConcurrentStudentCoverageRatio">Max Concurrent Senior Student Coverage</label>
            <input
              type="number"
              id="maxConcurrentStudentCoverageRatio"
              name="maxConcurrentStudentCoverageRatio"
              value={value.maxConcurrentStudentCoverageRatio}
              onChange={handleChange}
              min="1"
              max="10"
              placeholder="Default: 2"
            />
            <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
              Max number of ORs with senior students this doctor can cover concurrently (default: 2)
            </small>
          </div>
        </>
      )}

      {(value.employeeType === 'nurse-student-junior' || value.employeeType === 'nurse-student-senior') && (
        <div className="form-group">
          <label htmlFor="studentCompetency">Student Competency</label>
          <select
            id="studentCompetency"
            name="studentCompetency"
            value={value.studentCompetency}
            onChange={handleChange}
          >
            <option value="">Not set</option>
            <option value="new">New (red — cannot function independently)</option>
            <option value="intermediate">Intermediate (yellow — must pair with a CRNA or solo doc)</option>
            <option value="green">Green (can run solo at 1 doc : 2 students)</option>
          </select>
          <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
            New/intermediate students must be paired with a CRNA or a solo doctor and don't change staffing ratios
          </small>
        </div>
      )}
    </>
  );
};

export default ProviderForm;
